"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const moment_1 = tslib_1.__importDefault(require("moment"));
const uuid_1 = require("uuid");
const BaseModel_1 = tslib_1.__importDefault(require("./BaseModel"));
const TimeEntry_1 = tslib_1.__importDefault(require("./TimeEntry"));
const TagRepository_1 = tslib_1.__importDefault(require("../repositories/TagRepository"));
const TimeEntryRepository_1 = tslib_1.__importDefault(require("../repositories/TimeEntryRepository"));
const createTask_graphql_1 = tslib_1.__importDefault(require("../gql/tasks/mutations/createTask.graphql"));
const updateTask_graphql_1 = tslib_1.__importDefault(require("../gql/tasks/mutations/updateTask.graphql"));
const deleteTask_graphql_1 = tslib_1.__importDefault(require("../gql/tasks/mutations/deleteTask.graphql"));
const taskCreated_1 = tslib_1.__importDefault(require("./task/subscriptions/taskCreated"));
const taskUpdated_1 = tslib_1.__importDefault(require("./task/subscriptions/taskUpdated"));
const taskDeleted_1 = tslib_1.__importDefault(require("./task/subscriptions/taskDeleted"));
const timeEntryCreated_1 = tslib_1.__importDefault(require("./task/subscriptions/timeEntryCreated"));
const timeEntryUpdated_1 = tslib_1.__importDefault(require("./task/subscriptions/timeEntryUpdated"));
const timeEntryStopped_1 = tslib_1.__importDefault(require("./task/subscriptions/timeEntryStopped"));
const timeEntryDeleted_1 = tslib_1.__importDefault(require("./task/subscriptions/timeEntryDeleted"));
const repositories_1 = require("../repositories");
class Task extends BaseModel_1.default {
    constructor(props) {
        super(props);
        this.subtasks = repositories_1.SubtaskRepository.fromArray((props === null || props === void 0 ? void 0 : props.subtasks) || []);
        this.tags = TagRepository_1.default.fromArray((props === null || props === void 0 ? void 0 : props.tags) || []);
        this.timeEntries = TimeEntryRepository_1.default.fromArray((props === null || props === void 0 ? void 0 : props.timeEntries) || []);
    }
    defaults() {
        return {
            uuid: '',
            title: '',
            description: '',
            dueDate: null,
            eta: null,
            score: null,
            kanbanColumnUuid: null,
            completed: false,
            isArchived: false,
            priority: null,
            position: null,
            users: [],
            votes: [],
            total: 0,
            limited: null,
            images: [],
            project: null,
            userUuid: '',
            timeEntries: [],
            tags: [],
            subtasks: [],
            archivedAt: null,
            createdAt: null,
        };
    }
    get hours() {
        return moment_1.default.duration(this.eta).hours();
    }
    get minutes() {
        return moment_1.default.duration(this.eta).minutes();
    }
    get timeLeft() {
        return this.eta * 1000 - this.elapsedTime;
    }
    get progress() {
        if (this.percentages > 100) {
            return 100;
        }
        return this.percentages;
    }
    get isExceeded() {
        return this.percentages > 100;
    }
    get percentages() {
        const { eta } = this;
        return Math.round((this.calculatedTotal.asMilliseconds() / eta) * 100);
    }
    get calculatedTotal() {
        const total = moment_1.default.duration(this.total);
        const activeTimeEntry = this.timeEntries.first(e => e.active);
        if (activeTimeEntry) {
            total.add(activeTimeEntry.getDuration());
        }
        return total;
    }
    get totalFormatted() {
        const total = this.calculatedTotal;
        if (total.asMinutes() < 1) {
            return `${Math.round(total.asSeconds())} sec`;
        }
        return `${(total.hours() > 0 ? `${total.hours()}  h ` : '')} ${total.minutes()} min `;
    }
    get etaFormatted() {
        const eta = moment_1.default.duration(this.eta);
        return `${(eta.hours() ? `${eta.hours()} h` : '')} ${eta.minutes()} m`;
    }
    static subscriptions() {
        return [
            taskCreated_1.default.call(this),
            taskUpdated_1.default.call(this),
            taskDeleted_1.default.call(this),
            timeEntryCreated_1.default.call(this),
            timeEntryUpdated_1.default.call(this),
            timeEntryStopped_1.default.call(this),
            timeEntryDeleted_1.default.call(this),
        ];
    }
    prepareVariables() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const task = yield this.getUpdateVariables();
            delete task.timeEntries;
            return {
                task,
            };
        });
    }
    stop() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const activeEntry = this.timeEntries.first(e => e.active);
            if (!activeEntry)
                return Promise.reject(new Error('No active entry, nothing to stop'));
            if (activeEntry.getDuration() < (60 * 1000)) {
                const uuid = yield activeEntry.delete();
                this.timeEntries = this.timeEntries.filter(e => e.uuid !== uuid);
                return activeEntry;
            }
            activeEntry.active = false;
            activeEntry.endTime = moment_1.default().toDate();
            this.total += activeEntry.getDuration();
            return activeEntry.update();
        });
    }
    start() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.timeEntries.count()) {
                const latest = this.timeEntries.last();
                if (moment_1.default().diff(latest.endTime, 'seconds') < 10) {
                    latest.active = true;
                    latest.endTime = null;
                    return latest.update();
                }
            }
            const timeEntry = new TimeEntry_1.default({
                uuid: uuid_1.v4(),
                startTime: moment_1.default().toDate(),
                endTime: null,
                task: {
                    uuid: this.uuid,
                },
                active: true,
            });
            this.timeEntries.push(yield timeEntry.create());
            return timeEntry;
        });
    }
    create() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.hydrate(yield this.mutate(createTask_graphql_1.default, yield this.prepareVariables()));
        });
    }
    update() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.hydrate(yield this.mutate(updateTask_graphql_1.default, yield this.prepareVariables()));
        });
    }
    delete() {
        const _super = Object.create(null, {
            delete: { get: () => super.delete }
        });
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return _super.delete.call(this, deleteTask_graphql_1.default);
        });
    }
}
exports.default = Task;
//# sourceMappingURL=Task.js.map